import { useEffect, useMemo, useState } from 'react';
import { startOfDay, subDays } from 'date-fns';
import { ThumbsDown, ThumbsUp, X } from 'lucide-react';
import { useUrlFilters } from '../../hooks/admin/useUrlFilters';
import { useHelpChatLogs, useHelpChatStats } from '../../hooks/admin/useAdminHelpChat';
import { booleanField, daysPreset, enumField, pageField, stringField } from '../../lib/adminFilters';
import { estimateLlmCostUsd, formatUsd, readLlmPricingFromEnv } from '../../lib/helpChatCost';
import { chartColors } from '../../lib/chartColors';
import { getApiErrorMessage } from '../../lib/apiError';
import { relativeTime as relative } from '../../lib/adminFormat';
import { moduleFromPath } from '../../lib/helpContext';
import { formatDateTime, truncate } from '../../lib/utils';
import DataTable from '../../components/ui/DataTable';
import StatTile from '../../components/admin/StatTile';
import DateRangeFilter from '../../components/admin/DateRangeFilter';
import SeriesChart from '../../components/admin/SeriesChart';
import Pagination from '../../components/admin/Pagination';
import InlineNotice from '../../components/admin/InlineNotice';
import HelpChatLogDetailModal from '../../components/admin/HelpChatLogDetailModal';
import { Pill, RolePill } from '../../components/admin/StatusBadge';
import type { HelpChatLogRow, HelpChatTopQuestion } from '../../types/admin';

const PAGE_SIZE = 25;
const DAYS_OPTIONS = [
  { value: '7', label: '7d' },
  { value: '30', label: '30d' },
  { value: '90', label: '90d' },
] as const;

const filterSchema = {
  days: enumField(['7', '30', '90'], '30'),
  search: stringField('', { maxLength: 200 }),
  role: enumField(['', 'ADMIN', 'AGENCY', 'BRAND', 'KOL'], ''),
  feedback: enumField(['', 'up', 'down', 'none'], ''),
  fallbackOnly: booleanField(false),
  organizationId: stringField(''),
  userId: stringField(''),
  sessionId: stringField(''),
  page: pageField(),
};

const pricing = readLlmPricingFromEnv();

function shortDay(value: string): string {
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return value;
  return d.toLocaleDateString('en-MY', { month: 'short', day: 'numeric' });
}

function TopQuestionsCard({
  title,
  items,
  loading,
  emptyMessage,
  onPick,
}: {
  title: string;
  items: HelpChatTopQuestion[];
  loading: boolean;
  emptyMessage: string;
  onPick: (q: HelpChatTopQuestion) => void;
}) {
  return (
    <div className="glass-card p-5">
      <h3 className="text-sm font-semibold text-glass-text">{title}</h3>
      {loading ? (
        <div className="mt-3 space-y-2">
          {Array.from({ length: 5 }).map((_, i) => (
            <div key={i} className="h-6 animate-pulse rounded bg-glass-3/60" />
          ))}
        </div>
      ) : items.length === 0 ? (
        <p className="mt-3 text-sm text-glass-text-3">{emptyMessage}</p>
      ) : (
        <ol className="mt-3 divide-y divide-glass">
          {items.slice(0, 8).map((q) => (
            <li key={q.normalized}>
              <button
                type="button"
                onClick={() => onPick(q)}
                className="flex w-full items-center justify-between gap-3 py-2 text-left text-sm transition hover:bg-glass-3/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent2/50"
                title={`Last asked ${relative(q.lastAskedAt)} — click to filter the table`}
              >
                <span className="min-w-0 flex-1 truncate text-glass-text">{q.normalized}</span>
                <span className="shrink-0 tabular-nums text-xs text-glass-text-3">{q.count}×</span>
              </button>
            </li>
          ))}
        </ol>
      )}
    </div>
  );
}

export default function AdminChatbotLog() {
  const { filters, setFilters, resetFilters } = useUrlFilters(filterSchema);
  const days = daysPreset(filters.days);

  const [searchInput, setSearchInput] = useState(filters.search);
  useEffect(() => {
    setSearchInput(filters.search);
  }, [filters.search]);
  useEffect(() => {
    if (searchInput === filters.search) return;
    const t = setTimeout(() => setFilters({ search: searchInput }), 300);
    return () => clearTimeout(t);
  }, [searchInput, filters.search, setFilters]);

  // Stable per calendar day so the query key does not churn every render.
  const from = useMemo(() => startOfDay(subDays(new Date(), days)).toISOString(), [days]);

  const statsQuery = useHelpChatStats(days);
  const logsQuery = useHelpChatLogs({
    from,
    role: filters.role || undefined,
    feedback: filters.feedback || undefined,
    fallbackOnly: filters.fallbackOnly || undefined,
    organizationId: filters.organizationId || undefined,
    userId: filters.userId || undefined,
    search: filters.search || undefined,
    sessionId: filters.sessionId || undefined,
    page: filters.page,
    limit: PAGE_SIZE,
  });

  const stats = statsQuery.data;
  const totals = stats?.totals;
  const votes = (totals?.up ?? 0) + (totals?.down ?? 0);
  const helpfulPct = votes > 0 ? Math.round(((totals?.up ?? 0) / votes) * 100) : null;
  const perDay = useMemo(() => stats?.perDay ?? [], [stats]);
  // Derive the fallback rate from the daily rows so we do not depend on whether
  // the API reports `fallbackRate` as a fraction or a percentage.
  const dayTotals = perDay.reduce(
    (acc, d) => ({ count: acc.count + (d.count ?? 0), fallback: acc.fallback + (d.fallback ?? 0) }),
    { count: 0, fallback: 0 }
  );
  const fallbackPct =
    dayTotals.count > 0
      ? (dayTotals.fallback / dayTotals.count) * 100
      : totals
        ? totals.fallbackRate <= 1
          ? totals.fallbackRate * 100
          : totals.fallbackRate
        : 0;
  const estCost = estimateLlmCostUsd(totals?.promptTokens, totals?.completionTokens, pricing);
  const chartData = useMemo(
    () => perDay.map((d) => ({ date: d.date, answered: Math.max(0, d.count - d.fallback), fallback: d.fallback })),
    [perDay]
  );

  const [selected, setSelected] = useState<HelpChatLogRow | null>(null);
  const rows = logsQuery.data?.items ?? [];

  const chips: Array<{ key: 'organizationId' | 'userId' | 'sessionId'; label: string; value: string }> = [];
  if (filters.organizationId) chips.push({ key: 'organizationId', label: 'Org', value: filters.organizationId });
  if (filters.userId) chips.push({ key: 'userId', label: 'User', value: filters.userId });
  if (filters.sessionId) chips.push({ key: 'sessionId', label: 'Session', value: filters.sessionId });

  const hasActiveFilters =
    Boolean(filters.search || filters.role || filters.feedback || filters.fallbackOnly) || chips.length > 0;

  return (
    <div className="space-y-6 text-glass-text">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="page-title">Chatbot log</h1>
          <p className="mt-1 text-sm text-glass-text-2">
            Every question asked to the myKOL Assistant, with feedback, fallbacks and cost.
          </p>
        </div>
        <DateRangeFilter value={filters.days} options={DAYS_OPTIONS} onChange={(v) => setFilters({ days: v })} label="Window" />
      </div>

      {statsQuery.isError && (
        <InlineNotice tone="error">{getApiErrorMessage(statsQuery.error, 'Failed to load chatbot stats')}</InlineNotice>
      )}

      <div className="grid grid-cols-2 gap-4 md:grid-cols-3 xl:grid-cols-5">
        <StatTile label="Questions" value={(totals?.count ?? 0).toLocaleString()} loading={statsQuery.isPending} sub={`last ${days} days`} />
        <StatTile
          label="Helpful"
          value={helpfulPct == null ? '—' : `${helpfulPct}%`}
          tone={helpfulPct == null ? 'muted' : helpfulPct >= 70 ? 'pos' : helpfulPct >= 40 ? 'warn' : 'danger'}
          loading={statsQuery.isPending}
          sub={`${totals?.up ?? 0} up · ${totals?.down ?? 0} down · ${totals?.none ?? 0} no vote`}
        />
        <StatTile
          label="Fallback rate"
          value={`${fallbackPct.toFixed(1)}%`}
          tone={fallbackPct > 30 ? 'danger' : fallbackPct > 15 ? 'warn' : 'pos'}
          loading={statsQuery.isPending}
          sub="answers without matching docs"
          onClick={() => setFilters({ fallbackOnly: true })}
          title="Show only fallback answers"
        />
        <StatTile
          label="Avg latency"
          value={`${Math.round(totals?.avgLatencyMs ?? 0).toLocaleString()} ms`}
          loading={statsQuery.isPending}
          tone={(totals?.avgLatencyMs ?? 0) > 6000 ? 'warn' : 'default'}
        />
        <StatTile
          label="LLM cost (est.)"
          value={formatUsd(estCost)}
          loading={statsQuery.isPending}
          sub={`${((totals?.promptTokens ?? 0) + (totals?.completionTokens ?? 0)).toLocaleString()} tokens · est.`}
          title={`Estimated at $${pricing.usdPer1MInput}/1M input and $${pricing.usdPer1MOutput}/1M output tokens`}
        />
      </div>

      <div className="glass-card p-5">
        <h3 className="mb-3 text-sm font-semibold text-glass-text">Questions per day</h3>
        <SeriesChart
          data={chartData}
          xKey="date"
          xFormatter={shortDay}
          loading={statsQuery.isPending}
          series={[
            { key: 'answered', name: 'Answered', color: chartColors.pos },
            { key: 'fallback', name: 'Fallback', color: chartColors.danger },
          ]}
        />
      </div>

      <div className="grid gap-4 lg:grid-cols-2">
        <TopQuestionsCard
          title="Top questions"
          items={stats?.topQuestions ?? []}
          loading={statsQuery.isPending}
          emptyMessage="No questions in this window."
          onPick={(q) => setFilters({ search: q.normalized, fallbackOnly: false })}
        />
        <TopQuestionsCard
          title="Unanswered (fallback)"
          items={stats?.topFallbackQuestions ?? []}
          loading={statsQuery.isPending}
          emptyMessage="No fallback answers — the docs are covering it."
          onPick={(q) => setFilters({ search: q.normalized, fallbackOnly: true })}
        />
      </div>

      <div className="glass-card space-y-3 p-4">
        <div className="grid gap-3 md:grid-cols-4">
          <input
            type="search"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            placeholder="Search questions and answers"
            className="glass-input w-full px-4 py-2 md:col-span-2"
          />
          <select
            value={filters.role}
            onChange={(e) => setFilters({ role: e.target.value as typeof filters.role })}
            className="glass-input w-full px-4 py-2"
          >
            <option value="">All roles</option>
            <option value="AGENCY">AGENCY</option>
            <option value="BRAND">BRAND</option>
            <option value="KOL">KOL</option>
            <option value="ADMIN">ADMIN</option>
          </select>
          <select
            value={filters.feedback}
            onChange={(e) => setFilters({ feedback: e.target.value as typeof filters.feedback })}
            className="glass-input w-full px-4 py-2"
          >
            <option value="">Any feedback</option>
            <option value="up">Thumbs up</option>
            <option value="down">Thumbs down</option>
            <option value="none">No feedback</option>
          </select>
        </div>
        <div className="flex flex-wrap items-center gap-3 text-sm">
          <label className="inline-flex items-center gap-2 text-glass-text-2">
            <input
              type="checkbox"
              checked={filters.fallbackOnly}
              onChange={(e) => setFilters({ fallbackOnly: e.target.checked })}
              className="rounded border-glass accent-accent2"
            />
            Fallback answers only
          </label>
          {chips.map((chip) => (
            <span
              key={chip.key}
              className="inline-flex items-center gap-1 rounded-full border border-glass bg-glass-2 px-2.5 py-1 text-xs text-glass-text"
            >
              <span className="text-glass-text-3">{chip.label}:</span>
              <span className="max-w-[160px] truncate font-mono" title={chip.value}>
                {chip.value}
              </span>
              <button
                type="button"
                onClick={() => setFilters({ [chip.key]: '' } as Partial<typeof filters>)}
                className="rounded-full p-0.5 text-glass-text-3 hover:bg-glass-3 hover:text-glass-text"
                aria-label={`Remove ${chip.label} filter`}
              >
                <X className="h-3 w-3" />
              </button>
            </span>
          ))}
          {hasActiveFilters && (
            <button
              type="button"
              onClick={() => resetFilters(['days'])}
              className="text-xs text-accent2 underline-offset-2 hover:underline"
            >
              Clear filters
            </button>
          )}
        </div>
      </div>

      {logsQuery.isError && (
        <InlineNotice tone="error">{getApiErrorMessage(logsQuery.error, 'Failed to load chatbot log')}</InlineNotice>
      )}

      <div className={`glass-card overflow-hidden ${logsQuery.isFetching && !logsQuery.isPending ? 'opacity-80' : ''}`}>
        <DataTable<HelpChatLogRow>
          data={rows}
          isLoading={logsQuery.isPending}
          emptyMessage="No questions match these filters."
          onRowClick={(row) => setSelected(row)}
          columns={[
            {
              header: 'Time',
              accessor: (r) => (
                <span title={formatDateTime(r.createdAt)} className="text-glass-text-2">
                  {relative(r.createdAt)}
                </span>
              ),
            },
            {
              header: 'User / org',
              accessor: (r) => (
                <div className="max-w-[200px]">
                  <div className="truncate text-glass-text" title={r.user?.email ?? undefined}>
                    {r.user?.fullName || r.user?.email || r.userId || 'Unknown'}
                  </div>
                  <div className="truncate text-xs text-glass-text-3">{r.organization?.name ?? '—'}</div>
                </div>
              ),
            },
            { header: 'Role', accessor: (r) => <RolePill role={r.role} /> },
            {
              header: 'Module',
              accessor: (r) => (
                <span title={r.route ?? undefined} className="text-glass-text-2">
                  {r.route ? moduleFromPath(r.route) : '—'}
                </span>
              ),
            },
            {
              header: 'Question',
              accessor: (r) => (
                <span className="block max-w-[360px] truncate" title={r.question}>
                  {truncate(r.question, 90)}
                </span>
              ),
            },
            {
              header: 'Feedback',
              accessor: (r) =>
                r.feedback === 'up' ? (
                  <ThumbsUp className="h-4 w-4 text-emerald-500" aria-label="Helpful" />
                ) : r.feedback === 'down' ? (
                  <ThumbsDown className="h-4 w-4 text-rose-500" aria-label="Not helpful" />
                ) : (
                  <span className="text-glass-text-3">—</span>
                ),
            },
            {
              header: 'Provider',
              accessor: (r) => (
                <span className="text-xs text-glass-text-2">
                  {r.provider || '—'}
                  {r.model ? <span className="text-glass-text-3"> · {r.model}</span> : null}
                </span>
              ),
            },
            {
              header: 'Latency',
              accessor: (r) => (
                <span className="inline-flex items-center gap-2 tabular-nums text-glass-text-2">
                  {r.latencyMs != null ? `${r.latencyMs.toLocaleString()} ms` : '—'}
                  {r.answerWasFallback && <Pill tone="warn">fallback</Pill>}
                </span>
              ),
            },
          ]}
        />
        <Pagination
          pagination={logsQuery.data?.pagination}
          onPageChange={(page) => setFilters({ page })}
          label="questions"
          isFetching={logsQuery.isFetching && !logsQuery.isPending}
        />
      </div>

      <HelpChatLogDetailModal
        log={selected}
        onClose={() => setSelected(null)}
        onFilterBySession={(sessionId) => {
          setSelected(null);
          setFilters({ sessionId, search: '', fallbackOnly: false, feedback: '', role: '' });
        }}
      />
    </div>
  );
}
